import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import ScreenContainer from "../components/screen";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("form");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!password || !confirmPassword) {
      setError("Please fill in both fields.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setStatus("loading");

    try {
      const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      if (res.ok) {
        setStatus("success");
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Reset failed. The link may have expired.");
        setStatus("form");
      }
    } catch {
      setError("Something went wrong. Please try again.");
      setStatus("form");
    }
  };

  if (!token) {
    return (
      <ScreenContainer>
        <section className="py-24 bg-white min-h-[50vh] flex items-center justify-center">
          <div className="max-w-md mx-auto text-center px-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Invalid link</h2>
            <p className="text-gray-600 mb-8">This password reset link is invalid. Please request a new one from the app.</p>
            <Link to="/" className="bg-orange-500 text-white px-6 py-3 rounded-full hover:bg-orange-600 transition inline-block">
              Go to homepage
            </Link>
          </div>
        </section>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      <section className="py-24 bg-white min-h-[50vh] flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-6 w-full">
          {status === "success" ? (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Password reset!</h2>
              <p className="text-gray-600 mb-8">Your password has been updated. You can now log in with your new password.</p>
              <Link to="/" className="bg-orange-500 text-white px-6 py-3 rounded-full hover:bg-orange-600 transition inline-block">
                Go to homepage
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Set new password</h2>
              <p className="text-gray-600 mb-8">Enter your new password below.</p>

              <form onSubmit={handleSubmit} className="space-y-4 text-left">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter new password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Confirm new password"
                  />
                </div>

                {error && (
                  <p className="text-red-600 text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full bg-orange-500 text-white py-3 rounded-full hover:bg-orange-600 transition disabled:opacity-50"
                >
                  {status === "loading" ? "Resetting..." : "Reset password"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </ScreenContainer>
  );
}
